import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/Shared";
import { Plus, Trash2, Mail, Phone, Pencil } from "lucide-react";
import { Modal } from "@/pages/Products";
import { toast } from "sonner";

const emptyForm = { name: "", contact_email: "", phone: "", category: "" };

export default function Suppliers() {
  const { user } = useAuth();
  const isAdmin = ["admin", "organization_admin", "platform_owner"].includes(user?.role);
  const [list, setList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const load = () => api.get("/suppliers").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (s) => {
    setForm({
      name: s.name || "",
      contact_email: s.contact_email || "",
      phone: s.phone || "",
      category: s.category || "",
    });
    setEditingId(s.id);
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.patch(`/suppliers/${editingId}`, form);
        toast.success("Leverancier bijgewerkt");
      } else {
        await api.post("/suppliers", form);
        toast.success("Leverancier toegevoegd");
      }
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Opslaan mislukt");
    }
  };

  const del = async (id) => {
    if (confirmDeleteId !== id) {
      setConfirmDeleteId(id);
      setTimeout(() => setConfirmDeleteId((c) => (c === id ? null : c)), 3500);
      return;
    }
    try {
      await api.delete(`/suppliers/${id}`);
      toast.success("Verwijderd");
      setConfirmDeleteId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="suppliers-page">
      <PageHeader
        overline="Inkoop"
        title="Leveranciers"
        description="Beheer al je leveranciers en hun contactgegevens."
        actions={isAdmin && (
          <button onClick={openCreate} className="inline-flex items-center gap-2 h-10 px-4 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded-md text-sm font-semibold" data-testid="suppliers-add-button">
            <Plus size={16} /> Nieuwe leverancier
          </button>
        )}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((s) => (
          <div key={s.id} className="card-flat p-5" data-testid={`supplier-card-${s.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <div className="overline text-[10px] mb-1">{s.category || "Algemeen"}</div>
                <h3 className="font-bold text-lg tracking-tight truncate">{s.name}</h3>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-1 ml-2 shrink-0">
                  <button onClick={() => openEdit(s)} className="text-zinc-500 hover:text-[#1B2D5C] p-1.5 hover:bg-zinc-100 rounded" data-testid={`edit-supplier-${s.id}`} title="Bewerken">
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => del(s.id)}
                    className={`p-1.5 rounded ${confirmDeleteId === s.id ? "bg-red-600 text-white hover:bg-red-700" : "text-zinc-400 hover:text-red-600 hover:bg-zinc-100"}`}
                    data-testid={`delete-supplier-${s.id}`}
                    title={confirmDeleteId === s.id ? "Klik nogmaals om te bevestigen" : "Verwijderen"}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
            </div>
            <div className="space-y-1.5 text-sm">
              {s.contact_email && <div className="flex items-center gap-2 text-zinc-600"><Mail size={13} /> {s.contact_email}</div>}
              {s.phone && <div className="flex items-center gap-2 text-zinc-600"><Phone size={13} /> {s.phone}</div>}
            </div>
          </div>
        ))}
        {list.length === 0 && (
          <div className="col-span-full py-12 text-center text-zinc-500 border border-dashed border-zinc-200 rounded-md">
            Nog geen leveranciers.
          </div>
        )}
      </div>

      {showForm && (
        <Modal onClose={() => { setShowForm(false); setEditingId(null); }} title={editingId ? "Leverancier bewerken" : "Nieuwe leverancier"}>
          <form onSubmit={submit} className="space-y-3" data-testid="supplier-form">
            <Input label="Naam *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="supplier-name" />
            <Input label="Categorie" value={form.category} onChange={(v) => setForm({ ...form, category: v })} testid="supplier-category" />
            <Input label="E-mail" type="email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} testid="supplier-email" />
            <Input label="Telefoon" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="supplier-phone" />
            <button type="submit" className="w-full h-10 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded-md text-sm font-semibold" data-testid="supplier-submit">
              {editingId ? "Wijzigingen opslaan" : "Toevoegen"}
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="overline block mb-1.5">{label}</label>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
        className="form-input" data-testid={testid}
      />
    </div>
  );
}
